<div *ngIf="!scrollableInput">
  <ng-container [ngTemplateOutlet]="errorMessages"></ng-container>
</div>

<div
  *ngIf="showUsageWarning && sessionType === sessionTypeEnum.Explorer"
  class="mb-3 text-center text-base font-medium text-amber-600"
>
  Create a new session to keep full context ({{
    contextUsageWarningPercentage
  }}% of limit used)
</div>

<div
  class="overflow-hidden rounded-3xl border-[1.5px]"
  [ngClass]="{
    'border-s300 focus-within:border-s500': !effectiveDisabled,
    'border-s200 bg-s100': effectiveDisabled
  }"
>
  <ng-scrollbar
    [orientation]="'vertical'"
    [visibility]="'hover'"
    [appearance]="'compact'"
    class="max-h-[300px] min-h-16"
  >
    <div class="flex w-full flex-row items-end pl-5 pr-2">
      <textarea
        class="text-s1 min-w-0 flex-1 resize-none bg-transparent py-4 text-xl leading-relaxed placeholder-gray-400 [field-sizing:content] focus:outline-none"
        placeholder="Ask anything..."
        [(ngModel)]="messageText"
        (keydown)="onKeyDown($event)"
        [disabled]="effectiveDisabled"
        rows="1"
      ></textarea>

      <button
        class="sticky bottom-2 mb-2 ml-3 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full focus:outline-none"
        [ngClass]="{
          'bg-m600 cursor-pointer':
            (isWorking && !messageText.trim()) ||
            (messageText.trim() && !effectiveDisabled && !!modelExtraId),
          'bg-s300 cursor-default':
            !(isWorking && !messageText.trim()) &&
            (!messageText.trim() || effectiveDisabled || !modelExtraId)
        }"
        (click)="isWorking && !messageText.trim() ? onStop() : onSend()"
        [disabled]="
          !(isWorking && !messageText.trim()) &&
          (!messageText.trim() || effectiveDisabled || !modelExtraId)
        "
      >
        <m-stop-icon
          *ngIf="isWorking && !messageText.trim()"
          class="text-white"
        ></m-stop-icon>
        <m-arrow-up-icon
          *ngIf="!(isWorking && !messageText.trim())"
          class="text-white"
        ></m-arrow-up-icon>
      </button>
    </div>
  </ng-scrollbar>
</div>

<div
  *ngIf="
    showSelects &&
    !(isRestrictedUser && sessionType === sessionTypeEnum.Explorer) &&
    (projectHasE2bApiKey || sessionType === sessionTypeEnum.Explorer)
  "
  class="mt-4 flex h-10 flex-row items-center justify-center space-x-3"
>
  <div
    *ngIf="sessionType !== sessionTypeEnum.Explorer"
    class="h-10 w-10 flex-shrink-0"
  >
    <m-auto-accept-toggle [sessionId]="sessionId"></m-auto-accept-toggle>
  </div>

  <div
    *ngIf="sessionType === sessionTypeEnum.Explorer"
    class="h-10 w-10 flex-shrink-0"
  ></div>

  <ng-select
    #sessionAgentSelect
    [fixNgSelectBottom]="sessionAgentSelect"
    *ngIf="sessionType !== sessionTypeEnum.Explorer"
    class="session-agents-select w-[140px] select-none text-base"
    [items]="agents"
    [clearable]="false"
    [searchable]="true"
    [searchFn]="agentsSearchFn"
    [(ngModel)]="agent"
    (ngModelChange)="onAgentSelect()"
    [disabled]="disabled"
    appendTo="body"
    placeholder="Agent"
  ></ng-select>

  <ng-select
    #sessionModelSelect
    [fixNgSelectBottom]="sessionModelSelect"
    class="session-models-select w-[340px] select-none text-base"
    [class.session-models-select-error]="!modelExtraId && messageText.trim()"
    [items]="models"
    bindLabel="label"
    bindValue="value"
    [groupBy]="'providerId'"
    [clearable]="false"
    [searchable]="true"
    [searchFn]="modelsSearchFn"
    [loading]="modelsLoading"
    [(ngModel)]="modelExtraId"
    (ngModelChange)="onModelSelect()"
    (open)="openModelSelect()"
    [disabled]="disabled"
    appendTo="body"
    placeholder="Select LLM"
  >
    <ng-template ng-label-tmp let-item="item">
      <div class="ml-1 flex items-center truncate">
        <m-provider-icon
          [providerId]="item.providerId"
          class="text-s500 mr-2 flex-shrink-0"
        ></m-provider-icon>
        <span class="truncate">{{ item.modelName }}</span>
      </div>
    </ng-template>

    <ng-template ng-optgroup-tmp let-item="item">
      <div class="flex items-center gap-1.5">
        <m-provider-icon
          [providerId]="item.providerId"
          class="text-s500 flex-shrink-0"
        ></m-provider-icon>
        <span>{{ providerNameById[item.providerId] }}</span>
      </div>
    </ng-template>

    <ng-template ng-option-tmp let-item="item">
      <div class="ml-1 flex items-center gap-2 truncate">
        <div class="flex min-w-0 items-center truncate">
          <span class="truncate">{{ item.modelName }}</span>
        </div>
        <span
          *ngIf="item.contextLimitFormatted"
          class="text-s400 ml-auto flex-shrink-0 text-sm"
          >{{ item.contextLimitFormatted }}</span
        >
      </div>
    </ng-template>
  </ng-select>

  <ng-select
    #sessionVariantSelect
    [fixNgSelectBottom]="sessionVariantSelect"
    class="session-variants-select w-[140px] select-none text-base"
    [items]="variants"
    bindLabel="label"
    bindValue="value"
    [clearable]="false"
    [searchable]="true"
    [searchFn]="variantsSearchFn"
    [(ngModel)]="variant"
    (ngModelChange)="onVariantSelect()"
    [disabled]="disabled"
    appendTo="body"
    placeholder="Thinking"
  >
    <ng-template ng-option-tmp let-item="item">
      <div class="flex items-center gap-2">
        <span>{{ item.label }}</span>
        <m-thumbs-up-icon
          *ngIf="item.isRecommended"
          class="ml-auto flex-shrink-0 text-gray-400"
        ></m-thumbs-up-icon>
      </div>
    </ng-template>
  </ng-select>

  <div class="h-10 w-10 flex-shrink-0">
    <m-context-usage-circle
      [modelExtraId]="modelExtraId"
      (usageWarningChange)="showUsageWarning = $event"
      (usageLimitReachedChange)="
        onUsageLimitReachedChange({ isReached: $event })
      "
    ></m-context-usage-circle>
  </div>
</div>

<div *ngIf="scrollableInput" class="mt-5">
  <ng-container [ngTemplateOutlet]="errorMessages"></ng-container>
</div>

<ng-template #errorMessages>
  <div
    *ngIf="isUsageLimitReached && sessionType === sessionTypeEnum.Explorer"
    class="mb-2 text-center text-[17px] text-red-500"
    >Context limit reached. Start a new session or select a model with a larger
    context window.</div
  >

  <div
    *ngIf="isRestrictedUser"
    class="mb-2 text-center text-[17px] text-red-500"
    >Demo user cannot start AI sessions</div
  >

  <div
    *ngIf="
      !isRestrictedUser && !isEditor && sessionType === sessionTypeEnum.Editor
    "
    class="mb-2 text-center text-[17px] text-red-500"
    >Editor session requires Files Editor role</div
  >

  <div
    *ngIf="
      !isRestrictedUser &&
      !isExplorer &&
      sessionType === sessionTypeEnum.Explorer
    "
    class="mb-2 text-center text-[17px] text-red-500"
    >Explorer session requires Explorer role</div
  >

  <div
    *ngIf="
      !isRestrictedUser &&
      isEditor &&
      !projectHasE2bApiKey &&
      sessionType === sessionTypeEnum.Editor
    "
    class="mb-2 text-center text-[17px] text-red-500"
    >Editor session requires E2B API Key (see Project Settings)</div
  >
</ng-template>
